import type { TaxAdvisorRouter } from "@fideus/tax-advisor-api";
import { registerTransformers } from "@fideus/temporal-superjson-transformers";
import { registerTaxIdTransformer } from "@fideus-core/company";
import { registerLoanAgreementTransformer } from "@fideus-core/loan-agreement";
import { registerAmountTransformer } from "@fideus-core/transaction";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { createTRPCClient, httpBatchLink } from "@trpc/client";
import { type ReactNode, useState } from "react";
import superjson from "superjson";

// Register all core transformers on the client
registerTransformers(
  superjson,
  registerAmountTransformer,
  registerTaxIdTransformer,
  registerLoanAgreementTransformer
);

import { useGetToken, useRedirectToAuth } from "@fideus/auth";
import {
  AuthRedirectEvent,
  ConnectionProblemEvent,
  getPosthog,
  PosthogAnalytics,
} from "../analytics";
import { isAuthError } from "./isAuthError";
import { isConnectionError } from "./isConnectionError";
import { isTimeoutError } from "./isTimeoutError";
import { TRPCFetchError } from "./TRPCFetchError";
import { TRPCTimeoutError } from "./TRPCTimeoutError";
import { TRPCProvider } from "./TrpcContext";

interface TrpcApiProviderProps {
  apiUrl: string;
  children: ReactNode;
}

const RETRIABLE_ERROR_MAX_RETRIES = 3;

function isRetriableError(error: unknown): boolean {
  return isConnectionError(error) || isTimeoutError(error);
}

async function handleFetchResponse(
  response: Response,
  url: URL | RequestInfo,
  redirectToAuth: (opts: { redirectBackTo: URL }) => Promise<void>
): Promise<Response> {
  if (response.status === 401 || response.status === 403) {
    const posthog = getPosthog();
    if (posthog) {
      new PosthogAnalytics(posthog, { userType: "taxAdvisor" }).track(
        new AuthRedirectEvent({
          status: response.status,
          url: String(url),
        })
      );
    }
    await redirectToAuth({
      redirectBackTo: new URL(window.location.href),
    });
    // Browser is redirecting to auth — return a pending promise to avoid
    // throwing an error that surfaces as unhandled in error tracking.
    return new Promise<Response>(() => {});
  }
  if (response.status === 504) {
    throw new TRPCTimeoutError({ url, status: response.status });
  }
  return response;
}

function getErrorUrl(error: Error): string | undefined {
  if (error instanceof TRPCFetchError) {
    return String(error.url);
  }
  if (error instanceof TRPCTimeoutError) {
    return String(error.url);
  }
  return undefined;
}

function trackRetriableProblem(error: Error, isFinalFailure: boolean) {
  const posthog = getPosthog();
  if (posthog) {
    new PosthogAnalytics(posthog, { userType: "taxAdvisor" }).track(
      new ConnectionProblemEvent({
        errorMessage: error.message,
        retryCount: RETRIABLE_ERROR_MAX_RETRIES,
        isFinalFailure,
        url: getErrorUrl(error),
      })
    );
  }
}

function makeQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        if (isRetriableError(error)) {
          trackRetriableProblem(error, true);
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        if (isRetriableError(error)) {
          trackRetriableProblem(error, true);
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 60_000 /* ms */,
        // Only throw non-retriable errors to error boundaries
        // Retriable errors (connection/timeout) are handled inline by components
        throwOnError: (error) =>
          !isRetriableError(error) && !isAuthError(error),
        // Retry connection and timeout errors
        retry: (failureCount, error) =>
          isRetriableError(error) && failureCount < RETRIABLE_ERROR_MAX_RETRIES,
        // Exponential backoff: 1s, 2s, 4s
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      },
      mutations: {
        // Only throw non-retriable errors to error boundaries
        // Retriable errors (connection/timeout) are handled inline by components
        throwOnError: (error) =>
          !isRetriableError(error) && !isAuthError(error),
        // Retry connection and timeout errors
        retry: (failureCount, error) =>
          isRetriableError(error) && failureCount < RETRIABLE_ERROR_MAX_RETRIES,
        // Exponential backoff: 1s, 2s, 4s
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined;

function getQueryClient() {
  if (typeof window === "undefined") {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

export function TrpcApiProvider({ apiUrl, children }: TrpcApiProviderProps) {
  const getToken = useGetToken();
  const redirectToAuth = useRedirectToAuth();

  const queryClient = getQueryClient();
  const [trpcClient] = useState(() =>
    createTRPCClient<TaxAdvisorRouter>({
      links: [
        httpBatchLink({
          url: apiUrl,
          async fetch(url, options) {
            try {
              const response = await fetch(url, {
                ...options,
                credentials: "include",
              });
              return await handleFetchResponse(response, url, redirectToAuth);
            } catch (error) {
              if (error instanceof TRPCTimeoutError) {
                throw error;
              }
              throw new TRPCFetchError({ error, url, options });
            }
          },
          transformer: superjson,
          headers: async () => {
            try {
              const token = await getToken();
              return {
                Authorization: `Bearer ${token}`,
              };
            } catch {
              // Token retrieval failed (e.g., session expired) - redirect to re-authenticate
              await redirectToAuth({
                redirectBackTo: new URL(window.location.href),
              });
              // Return empty headers - redirect will navigate away
              return {};
            }
          },
        }),
      ],
    })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {children}
      </TRPCProvider>
    </QueryClientProvider>
  );
}
